import { DirectionalLight, type PerspectiveCamera, type Scene } from 'three/webgpu';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';

import type { ShadowConfig } from '../../settings/presets.js';

const KEY_COLOR = 0xfff1e0;
const KEY_INTENSITY = 2.4;
const KEY_POSITION = [40, 70, 25] as const;

/**
 * The directional key light with cascaded shadow maps (Section 4: 3 cascades, 2048 at Ultra).
 * Map size changes live; cascade count or fade changes rebuild the light so the lighting nodes
 * pick up the new shadow node.
 */
export class ShadowRig {
  private light: DirectionalLight | null = null;
  private csm: CSMShadowNode | null = null;
  private config: ShadowConfig | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly camera: PerspectiveCamera,
  ) {}

  apply(cfg: ShadowConfig | null): void {
    const sameShape =
      this.config !== null &&
      cfg !== null &&
      this.config.cascades === cfg.cascades &&
      this.config.fade === cfg.fade &&
      this.config.maxFar === cfg.maxFar;
    if (this.light && this.config && cfg && sameShape) {
      if (this.config.mapSize !== cfg.mapSize) {
        this.light.shadow.mapSize.set(cfg.mapSize, cfg.mapSize);
        for (const cascadeLight of this.csm?.lights ?? []) {
          const shadow = cascadeLight.shadow;
          if (!shadow) continue;
          shadow.mapSize.set(cfg.mapSize, cfg.mapSize);
          shadow.map?.dispose();
          shadow.map = null;
        }
      }
      this.config = cfg;
      return;
    }
    this.remove();
    const light = new DirectionalLight(KEY_COLOR, KEY_INTENSITY);
    light.position.set(...KEY_POSITION);
    if (cfg) {
      light.castShadow = true;
      light.shadow.mapSize.set(cfg.mapSize, cfg.mapSize);
      light.shadow.bias = -0.0002;
      light.shadow.normalBias = 0.06;
      light.shadow.camera.near = 1;
      light.shadow.camera.far = 400;
      const csm = new CSMShadowNode(light, {
        cascades: cfg.cascades,
        maxFar: cfg.maxFar,
        mode: 'practical',
        lightMargin: 60,
      });
      csm.fade = cfg.fade;
      light.shadow.shadowNode = csm;
      this.csm = csm;
    }
    this.scene.add(light);
    this.light = light;
    this.config = cfg;
  }

  /** Call after the camera's projection changes (resize, FOV) so the cascades refit. */
  onCameraChange(): void {
    if (this.csm && this.csm.camera === this.camera) this.csm.updateFrustums();
  }

  dispose(): void {
    this.remove();
  }

  private remove(): void {
    if (this.light) {
      this.scene.remove(this.light);
      this.light.shadow.map?.dispose();
      this.light.dispose();
    }
    this.csm = null;
    this.light = null;
    this.config = null;
  }
}
